import { action } from '@ember/object';
import Component from '@glimmer/component';
import { cached, tracked } from '@glimmer/tracking';
import type { JournalReportDefaultIntervalItem } from 'donor-elf-web/lib/journal-utils';
import JournalStageModel from 'donor-elf-web/models/journal-stage';
import Highcharts from 'highcharts';

interface ComponentArgs {
  journalStage?: JournalStageModel; // expected to be undefined when @total={{true}}
  total: boolean; // when true, then it shows the total work done in all the stages
  formattedReport: any;
  selectedIntervalItems: JournalReportDefaultIntervalItem[];
  intervalTimespan: 'week' | 'day';
  chartHeight?: number; // default 45
  showTotalOnColumn?: boolean;
}

export default class JournalReportCompletedStageChargeComponent extends Component<ComponentArgs> {
  chartElement: any;
  @tracked tooltipAttemptedCount?: number;
  @tracked tooltipCompletedCount?: number;
  @tracked tooltipTotalCount?: number;
  @tracked tooltipDate?: string;

  @action
  renderChart(e: any) {
    this.chartElement = e;
    Highcharts.chart(this.chartElement, this.chartSettings);
  }

  get chartHeight() {
    return this.args.chartHeight ?? 45;
  }

  get isDecisionStage() {
    return this.args.journalStage?.decisionStage;
  }

  get isWeekIntervalTimespan() {
    return this.args.intervalTimespan === 'week';
  }

  @cached
  get tasksCountsByIntervalDate(): {
    [date: string]: { attempted: number; completed: number; total: number };
  } {
    let byDate: {
      [date: string]: { attempted: number; completed: number; total: number };
    } = {};
    let attempted = 0;
    let completed = 0;
    let total = 0;
    this.args.selectedIntervalItems.forEach((intervalItem) => {
      attempted = 0;
      completed = 0;
      total = 0;
      if (this.args.total) {
        Object.keys(intervalItem.completedStageWork).forEach((key) => {
          if (key != 'maxTotal') {
            attempted += intervalItem.completedStageWork[key].attempted;
            completed += intervalItem.completedStageWork[key].completed;
          }
        });
        completed += intervalItem.decisions.allGoals.totalCount;
        total = attempted + completed;
      } else {
        if (this.isDecisionStage) {
          completed = intervalItem.decisions.allGoals.totalCount;
          total = completed;
        } else {
          attempted =
            intervalItem.completedStageWork[this.args.journalStage!.id]
              ?.attempted || 0;
          completed =
            intervalItem.completedStageWork[this.args.journalStage!.id]
              ?.completed || 0;
          total = attempted + completed;
        }
      }
      byDate[intervalItem.date] = {
        attempted,
        completed,
        total,
      };
    });
    return byDate;
  }

  get attemptedSeries() {
    let data: any[] = [];
    this.args.selectedIntervalItems.forEach((item, index) => {
      let partialInterval =
        index === this.args.selectedIntervalItems.length - 1 &&
        item !== this.args.formattedReport.lastFullIntervalItem;
      let color = partialInterval ? '#d1d3d5' : '#8ac7ec';
      let taskCounts = this.tasksCountsByIntervalDate[item.date];
      let formattedDate = item.dateRangeFormatted;
      data.push({
        y: taskCounts.attempted,
        color,
        events: {
          click: () => {
            this.tooltipDate = formattedDate;
            this.tooltipAttemptedCount = taskCounts.attempted;
            this.tooltipCompletedCount = taskCounts.completed;
            this.tooltipTotalCount = taskCounts.total;
          },
          mouseOver: () => {
            this.tooltipDate = formattedDate;
            this.tooltipAttemptedCount = taskCounts.attempted;
            this.tooltipCompletedCount = taskCounts.completed;
            this.tooltipTotalCount = taskCounts.total;
          },
          mouseOut: () => {
            this.tooltipDate = undefined;
            this.tooltipAttemptedCount = undefined;
            this.tooltipCompletedCount = undefined;
            this.tooltipTotalCount = undefined;
          },
        },
      });
    });
    return {
      name: 'Attempted',
      type: 'column',
      color: '#6c757d',
      pointPadding: 0.0,
      data,
      events: {
        mouseOut: () => {
          this.tooltipDate = undefined;
          this.tooltipAttemptedCount = undefined;
          this.tooltipCompletedCount = undefined;
          this.tooltipTotalCount = undefined;
        },
      },
    };
  }

  get completedSeries() {
    let data: any[] = [];
    this.args.selectedIntervalItems.forEach((item, index) => {
      let partialInterval =
        index === this.args.selectedIntervalItems.length - 1 &&
        item !== this.args.formattedReport.lastFullIntervalItem;
      let color = partialInterval ? '#adb5bd' : '#0b68a1';
      let taskCounts = this.tasksCountsByIntervalDate[item.date];
      let formattedDate = item.dateRangeFormatted;
      data.push({
        y: taskCounts.completed,
        color,
        events: {
          click: () => {
            this.tooltipDate = formattedDate;
            this.tooltipAttemptedCount = taskCounts.attempted;
            this.tooltipCompletedCount = taskCounts.completed;
            this.tooltipTotalCount = taskCounts.total;
          },
          mouseOver: () => {
            this.tooltipDate = formattedDate;
            this.tooltipAttemptedCount = taskCounts.attempted;
            this.tooltipCompletedCount = taskCounts.completed;
            this.tooltipTotalCount = taskCounts.total;
          },
          mouseOut: () => {
            this.tooltipDate = undefined;
            this.tooltipAttemptedCount = undefined;
            this.tooltipCompletedCount = undefined;
            this.tooltipTotalCount = undefined;
          },
        },
      });
    });
    return {
      type: 'column',
      color: '#6c757d',
      pointPadding: 0.0,
      data,
      events: {
        mouseOut: () => {
          this.tooltipDate = undefined;
          this.tooltipAttemptedCount = undefined;
          this.tooltipCompletedCount = undefined;
          this.tooltipTotalCount = undefined;
        },
      },
    };
  }

  get goalSeries() {
    let weeklyGoal = this.args.journalStage!.weeklyGoal;
    let data: any[] = [];

    this.args.selectedIntervalItems.forEach((item) => {
      let taskCounts = this.tasksCountsByIntervalDate[item.date];
      let formattedDate = item.dateRangeFormatted;
      data.push({
        y: weeklyGoal,
        events: {
          click: () => {
            this.tooltipDate = formattedDate;
            this.tooltipAttemptedCount = taskCounts.attempted;
            this.tooltipCompletedCount = taskCounts.completed;
            this.tooltipTotalCount = taskCounts.total;
          },
          mouseOver: () => {
            this.tooltipDate = formattedDate;
            this.tooltipAttemptedCount = taskCounts.attempted;
            this.tooltipCompletedCount = taskCounts.completed;
            this.tooltipTotalCount = taskCounts.total;
          },
          mouseOut: () => {
            this.tooltipDate = undefined;
            this.tooltipAttemptedCount = undefined;
            this.tooltipCompletedCount = undefined;
            this.tooltipTotalCount = undefined;
          },
        },
      });
    });

    return {
      type: 'line',
      name: 'Goal',
      color: '#f0ad4e',
      lineWidth: 2,
      marker: { enabled: false },
      data,
      events: {
        mouseOut: () => {
          this.tooltipDate = undefined;
          this.tooltipAttemptedCount = undefined;
          this.tooltipCompletedCount = undefined;
          this.tooltipTotalCount = undefined;
        },
      },
    };
  }

  get plotOptions() {
    let options: any = {
      column: {
        stacking: 'normal',
      },
      series: {
        animation: false,
        states: {
          hover: {
            enabled: true,
          },
        },
      },
    };
    if (this.args.showTotalOnColumn) {
      options.column.dataLabels = {
        enabled: true,
        formatter: function (): any {
          if (this.series.name === 'Attempted') {
            return this.point.stackTotal;
          } else {
            return null;
          }
        },
      };
    }
    return options;
  }

  get chartSettings() {
    let { attemptedSeries, completedSeries } = this;
    let yMax = 1;
    let series: any[] = [attemptedSeries, completedSeries];
    if (this.isWeekIntervalTimespan && this.args.journalStage?.weeklyGoal) {
      series.push(this.goalSeries);
      yMax = this.args.journalStage.weeklyGoal;
    }
    this.args.selectedIntervalItems.forEach((item) => {
      if (this.tasksCountsByIntervalDate[item.date].total > yMax) {
        yMax = this.tasksCountsByIntervalDate[item.date].total;
      }
    });
    return {
      chart: {
        animation: false,
        height: this.chartHeight,
        width: 200,
        margin: [2, 0, 2, 0],
        borderWidth: 0,
      },
      title: {
        text: undefined,
      },
      tooltip: {
        enabled: false,
      },
      credits: {
        enabled: false,
      },
      xAxis: {
        labels: {
          enabled: false,
        },
        visible: false,
        min: 0,
        max: this.args.selectedIntervalItems.length - 1,
      },
      yAxis: {
        min: 0,
        labels: {
          enabled: false,
        },
        max: yMax,
        title: {
          text: null,
        },
        startOnTick: false,
        endOnTick: false,
        tickPositions: [0],
      },
      legend: {
        enabled: false,
      },
      plotOptions: this.plotOptions,
      series,
    } as Highcharts.Options;
  }
}
